import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  manpowerApi,
  lookupsApi,
  TRAVEL_PREFS,
  type TravelPref,
  type TravelRosterRow,
  type TravelRosterInput,
  type LookupOption,
} from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate, toDateInputValue } from '@/lib/dates';

/**
 * Travel roster — who will travel, and from when (#396 slice 1).
 *
 * The supply side of the traveling-manpower gap analysis. Demand already
 * exists in the crew grid; this is the half PMT had no source for.
 *
 * ── Three things this view must never quietly do ────────────────────────────
 *
 * 1. ⚠️ SUM THE TIERS. 'Traveler', '50/50' and 'Local' are reported side by
 *    side and never added together into one "travelers" figure. The manpower
 *    lead keeps occasional travelers off the availability numbers on purpose —
 *    "I tend to leave them off any lists of travelers so that we don't over
 *    commit them out of town" — and folding 50/50 in would overstate supply by 50 people
 *    against a roster of 114, in the direction that stops a hire happening.
 *
 * 2. ⚠️ PRESENT THE AVAILABILITY COUNT AS DERIVED. `availableFrom` is typed by
 *    hand. PMT cannot compute it: the crew schedule records how many bodies per
 *    day, never which people. The banner below says so, and it stays until the
 *    day that stops being true.
 *
 * 3. ⚠️ RENAME '50/50'. It is the source vocabulary, slash and all.
 *
 * ── On blanks ───────────────────────────────────────────────────────────────
 * An empty `availableFrom` means AVAILABLE NOW, not unknown. That is the one
 * reading that makes the column safe to leave blank for the 100+ people who
 * are not on a fixed end date, and the cell says "Now" rather than "—" so the
 * distinction is visible rather than inferred.
 */

// ─── Shared style constants (matched to ManpowerPage / LeadersView) ──────────

const TH: React.CSSProperties = {
  background: 'var(--ew-dark)',
  color: 'var(--ew-white)',
  fontWeight: 600,
  fontSize: 11,
  padding: '8px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
};

const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)',
  borderRight: '1px solid var(--ew-n-100)',
  padding: '7px 10px',
  fontSize: 12,
  verticalAlign: 'middle',
};

const INPUT: React.CSSProperties = {
  fontFamily: 'var(--ew-font)',
  fontSize: 12,
  padding: '5px 8px',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-md)',
  background: 'var(--ew-white)',
  color: 'var(--ew-n-700)',
  width: '100%',
  boxSizing: 'border-box',
};

const SELECT: React.CSSProperties = { ...INPUT, maxWidth: 260, width: 'auto' };

/** Tier colours. Deliberately not a red/amber/green ramp — these are categories, not a scale. */
const PREF_STYLE: Record<TravelPref, React.CSSProperties> = {
  'Traveler': { background: 'var(--ew-quality-100)', color: 'var(--ew-quality-700)' },
  '50/50':    { background: 'var(--ew-n-100)', color: 'var(--ew-n-700)' },
  'Local':    { background: 'transparent',              color: 'var(--ew-n-500)' },
};

export function prefLabel(p: TravelPref): string {
  return p;
}

/**
 * What an empty availability date means, in the one place that decides it.
 * Exported so the test pins the reading rather than the rendering.
 */
export function availabilityLabel(availableFrom: string | null): string {
  return availableFrom ? fmtDate(availableFrom) : 'Now';
}

/**
 * Whether someone counts as free by a given day. Mirrors the API's predicate
 * (`available_from IS NULL OR available_from <= @on`) — the two have to agree
 * or the on-screen count disagrees with the gap view built on the same rule.
 */
export function isFreeBy(row: Pick<TravelRosterRow, 'availableFrom'>, onYmd: string): boolean {
  return row.availableFrom === null || row.availableFrom <= onYmd;
}

type EditTarget = { row: TravelRosterRow | null } | null;

export default function TravelRosterView({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [prefFilter, setPrefFilter] = useState<TravelPref | ''>('');
  const [divFilter, setDivFilter] = useState<number | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  const [edit, setEdit] = useState<EditTarget>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['travel-roster', prefFilter, divFilter, showInactive],
    queryFn: () => manpowerApi.travelRoster({
      travelPref: prefFilter || undefined,
      divisionId: divFilter === '' ? undefined : divFilter,
      includeInactive: showInactive,
      limit: 500,
    }),
  });

  const { data: divisions = [] } = useQuery<LookupOption[]>({
    queryKey: ['lookups-divisions'],
    queryFn: lookupsApi.listDivisions,
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.rows ?? [];

  // Counted from the loaded rows rather than fetched separately, so the tiles
  // can never disagree with the table underneath them.
  const counts = useMemo(() => {
    const active = rows.filter((r) => r.isActive);
    const byPref = Object.fromEntries(TRAVEL_PREFS.map((p) => [p, 0])) as Record<TravelPref, number>;
    for (const r of active) if (r.travelPref in byPref) byPref[r.travelPref]++;
    return {
      byPref,
      total: active.length,
      // Reported on its own, never merged into a tier — the leading hypothesis
      // for the flat 14 and still an open question on #396.
      fullTimeTravelers: active.filter(
        (r) => r.travelPref === 'Traveler' && r.employmentType === 'Full Time',
      ).length,
    };
  }, [rows]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['travel-roster'] });
    qc.invalidateQueries({ queryKey: ['travel-roster-supply'] });
  };

  const deactivate = useMutation({
    mutationFn: (id: number) => manpowerApi.deactivateTravelRosterRow(id),
    onSuccess: invalidate,
  });

  return (
    <div>
      {/* ── The banner that must not be removed ────────────────────────────
          Every count on this page rests on a hand-typed date. Saying so is not
          a caveat, it is the difference between a planning number and a
          measurement, and the reader cannot tell them apart from the digits. */}
      <div
        role="note"
        style={{
          marginBottom: 12, padding: '8px 12px', fontSize: 12, lineHeight: 1.5,
          background: 'var(--ew-safety-50)',
          border: '1px solid var(--ew-safety-100)',
          borderRadius: 'var(--ew-r-md)',
          color: 'var(--ew-n-700)',
        }}
      >
        <strong>Availability here is maintained, not calculated.</strong>{' '}
        The crew schedule records how many people are on a job each day, never
        which people, so the Project Tracker cannot work out when someone comes free. These
        dates are only as current as the last time somebody updated them.
      </div>

      {/* ── Tiles. Tiers side by side, never summed. ─────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {TRAVEL_PREFS.map((p) => (
          <Tile key={p} label={p} value={counts.byPref[p]} accent={PREF_STYLE[p]} />
        ))}
        <Tile label="On the roster" value={counts.total} />
        <Tile
          label="Traveler + Full Time"
          value={counts.fullTimeTravelers}
          hint="Excludes temps. Matches the 14 in the roster workbook — see #396."
        />
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
          Travel{' '}
          <select
            aria-label="Filter by travel preference"
            style={SELECT}
            value={prefFilter}
            onChange={(e) => setPrefFilter(e.target.value as TravelPref | '')}
          >
            <option value="">All</option>
            {TRAVEL_PREFS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        <label style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
          Division{' '}
          <select
            aria-label="Filter by division"
            style={SELECT}
            value={divFilter}
            onChange={(e) => setDivFilter(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">All</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </label>

        <label style={{ fontSize: 12, color: 'var(--ew-n-500)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show former staff
        </label>

        {canEdit && (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginLeft: 'auto', fontSize: 12 }}
            onClick={() => setEdit({ row: null })}
          >
            Add person
          </button>
        )}
      </div>

      {isLoading && <p style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>Loading the roster…</p>}
      {isError && (
        <p style={{ fontSize: 12, color: 'var(--ew-danger)' }}>
          The roster could not be loaded. Nothing has been changed.
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
          {prefFilter || divFilter !== '' || showInactive
            ? 'Nobody on the roster matches these filters.'
            : 'The travel roster is empty. Seed it from the Field Roster export, or add people one at a time.'}
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={TH}>Name</th>
                <th style={TH}>Position</th>
                <th style={TH}>Division</th>
                <th style={TH}>Type</th>
                <th style={TH}>Travel</th>
                <th style={TH}>TWIC</th>
                <th style={TH}>Weekend</th>
                {/* ⚠️ "Vehicle", not "Fuel", and the tooltip no longer names a
                    payment at all. It used to say the 46-75 band pays fuel to
                    anyone without one; that stopped being true on 2026-09-22,
                    when the $20 became payable regardless of a company truck.
                    The column still records a real fact schedulers need — it
                    simply is not a pay field, and never read as one. */}
                <th style={TH} title="Has a company vehicle. Recorded for scheduling; it does not change travel pay.">
                  Vehicle
                </th>
                <th style={TH}>Home</th>
                <th style={TH}>Available from</th>
                {canEdit && <th style={TH} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ opacity: r.isActive ? 1 : 0.5 }}>
                  <td style={{ ...TD, fontWeight: 600 }}>
                    {r.name ?? '—'}
                    {!r.isActive && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--ew-n-500)' }}>(former)</span>
                    )}
                    {/* Surfaced, not hidden: these are the rows a re-import
                        duplicates, because there is nothing to match them on. */}
                    {r.employeeId === null && (
                      <span
                        title="No employee id — cannot be matched on a re-import"
                        style={{ marginLeft: 6, fontSize: 10, color: 'var(--ew-safety-700)' }}
                      >
                        no id
                      </span>
                    )}
                  </td>
                  <td style={TD}>{r.position ?? '—'}</td>
                  <td style={TD}>{r.divisionLabel ?? '—'}</td>
                  <td style={TD}>{r.employmentType ?? '—'}</td>
                  <td style={TD}>
                    <span style={{
                      ...PREF_STYLE[r.travelPref], padding: '2px 7px',
                      borderRadius: 'var(--ew-r-sm)', fontSize: 11, fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}>
                      {prefLabel(r.travelPref)}
                    </span>
                  </td>
                  <td style={TD}>{r.twic ? 'Yes' : '—'}</td>
                  <td style={TD}>{r.weekendOk ? 'Yes' : '—'}</td>
                  <td style={TD} title={r.companyVehicleReason}>
                    {r.hasCompanyVehicle ? 'Yes' : '—'}
                    {/* A human's answer and a job title's implication are not
                        the same claim, and only the first is safe to leave
                        alone. The marker is how a scheduler tells which rows
                        they have already been through. */}
                    {r.companyVehicleSource === 'scheduler-override' && (
                      <span
                        title="Set by a scheduler, not derived from the job title"
                        style={{ marginLeft: 6, fontSize: 10, color: 'var(--ew-n-500)' }}
                      >
                        set
                      </span>
                    )}
                    {/* Same treatment as the "no id" badge above: surfaced
                        rather than hidden, because the value shown is a guess.
                        Nine active rows are in this state today. */}
                    {r.companyVehicleNeedsReview && (
                      <span
                        title={r.companyVehicleReason}
                        style={{ marginLeft: 6, fontSize: 10, color: 'var(--ew-safety-700)' }}
                      >
                        check
                      </span>
                    )}
                  </td>
                  <td style={TD}>{r.homeState ?? '—'}</td>
                  <td style={{ ...TD, color: r.availableFrom ? 'var(--ew-n-700)' : 'var(--ew-n-500)' }}>
                    {availabilityLabel(r.availableFrom)}
                  </td>
                  {canEdit && (
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: 11 }}
                        onClick={() => setEdit({ row: r })}
                      >
                        Edit
                      </button>
                      {r.isActive && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 11, marginLeft: 4 }}
                          onClick={() => {
                            if (window.confirm(
                              `Mark ${r.name ?? 'this person'} as former staff? ` +
                              'They drop out of the counts and out of the Travel Acknowledgments picker, ' +
                              'but the row is kept rather than deleted, so anything already issued to them ' +
                              'stays on file. "Show former staff" brings them back into view.',
                            )) deactivate.mutate(r.id);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <EditModal
          row={edit.row}
          divisions={divisions}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); invalidate(); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, accent, hint }: {
  label: string; value: number; accent?: React.CSSProperties; hint?: string;
}) {
  return (
    <div
      title={hint}
      style={{
        border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
        padding: '8px 14px', minWidth: 110, background: 'var(--ew-white)',
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--ew-n-500)', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, ...(accent ?? {}), background: 'transparent' }}>
        {value}
      </div>
    </div>
  );
}

/**
 * Add / edit one person.
 *
 * All fields submit together in a single PATCH, the same reason the Leaders
 * modal does: travel preference and availability are one story about where
 * somebody can be sent, and saving them one at a time walks the row through
 * states that are individually valid and jointly wrong.
 */
function EditModal({ row, divisions, onClose, onSaved }: {
  row: TravelRosterRow | null;
  divisions: LookupOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });
  const [form, setForm] = useState<TravelRosterInput>({
    employeeId: row?.employeeId ?? '',
    firstName: row?.firstName ?? '',
    lastName: row?.lastName ?? '',
    position: row?.position ?? '',
    divisionId: row?.divisionId ?? null,
    employmentType: row?.employmentType ?? '',
    travelPref: row?.travelPref ?? 'Local',
    twic: row?.twic ?? false,
    weekendOk: row?.weekendOk ?? false,
    // ⚠️ `?? null`, never `?? false`. null is "nobody has said" and is the
    // correct starting state for a new person; false would pin them to "no
    // vehicle" and stop the job-title default ever running for them.
    hasCompanyVehicle: row?.hasCompanyVehicleOverride ?? null,
    homeState: row?.homeState ?? '',
    availableFrom: row?.availableFrom ? toDateInputValue(row.availableFrom) : '',
    notes: row?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof TravelRosterInput>(k: K, v: TravelRosterInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const payload: TravelRosterInput = {
        ...form,
        // '' clears the field rather than writing an empty string.
        employeeId: form.employeeId || null,
        position: form.position || null,
        employmentType: form.employmentType || null,
        homeState: form.homeState || null,
        notes: form.notes || null,
        availableFrom: form.availableFrom || null,
        // ⚠️ hasCompanyVehicle rides the spread UNTOUCHED and must keep doing
        // so. The `|| null` idiom above is right for strings and catastrophic
        // here: `false || null` is null, which would turn a scheduler's
        // explicit "no company vehicle" into "nobody has said" on every save.
      };
      return row
        ? manpowerApi.updateTravelRosterRow(row.id, payload)
        : manpowerApi.createTravelRosterRow(payload);
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Could not save. Nothing has been changed.'),
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={row ? `Edit ${row.name ?? 'roster entry'}` : 'Add someone to the travel roster'}
        style={{
          background: 'var(--ew-white)', borderRadius: 'var(--ew-r-lg)',
          padding: 20, width: 'min(560px, 92vw)', maxHeight: '88vh', overflowY: 'auto',
        }}
      >
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>
          {row ? row.name ?? 'Edit roster entry' : 'Add someone to the travel roster'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="First name">
            <input style={INPUT} value={form.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field label="Last name">
            <input style={INPUT} value={form.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} />
          </Field>

          <Field label="Employee ID" hint="Links to Paycor. Blank means a re-import cannot match this person.">
            <input style={INPUT} value={form.employeeId ?? ''} onChange={(e) => set('employeeId', e.target.value)} />
          </Field>
          <Field label="Position">
            <input style={INPUT} value={form.position ?? ''} onChange={(e) => set('position', e.target.value)} />
          </Field>

          <Field label="Division">
            <select
              style={INPUT}
              value={form.divisionId ?? ''}
              onChange={(e) => set('divisionId', e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">—</option>
              {divisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </Field>
          <Field label="Employment type">
            <select style={INPUT} value={form.employmentType ?? ''} onChange={(e) => set('employmentType', e.target.value)}>
              <option value="">—</option>
              <option value="Full Time">Full Time</option>
              <option value="Temp">Temp</option>
            </select>
          </Field>

          <Field label="Travel preference" hint="The roster's own wording. Do not rename these.">
            <select
              style={INPUT}
              value={form.travelPref}
              onChange={(e) => set('travelPref', e.target.value as TravelPref)}
            >
              {TRAVEL_PREFS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Home state">
            <input style={INPUT} value={form.homeState ?? ''} onChange={(e) => set('homeState', e.target.value)} />
          </Field>

          <Field
            label="Available from"
            hint="Leave blank if they are free now. Blank means available, not unknown."
          >
            <input
              type="date"
              style={INPUT}
              value={form.availableFrom ?? ''}
              onChange={(e) => set('availableFrom', e.target.value)}
            />
          </Field>

          {/* ⚠️ A SELECT, NOT A CHECKBOX, because there are three states and a
              checkbox can only carry two. "Unticked" would have to mean either
              "no vehicle" or "nobody has said", and collapsing them stops the
              job-title default ever running again for that person. */}
          <Field
            label="Company vehicle"
            hint={row
              ? row.companyVehicleReason
              : 'Left on the default, this follows the Paycor job title.'}
          >
            <select
              style={INPUT}
              value={form.hasCompanyVehicle == null ? '' : form.hasCompanyVehicle ? 'yes' : 'no'}
              onChange={(e) => set(
                'hasCompanyVehicle',
                e.target.value === '' ? null : e.target.value === 'yes',
              )}
            >
              <option value="">Use the job title</option>
              <option value="yes">Has a company vehicle</option>
              <option value="no">No company vehicle</option>
            </select>
          </Field>

          <Field label="Also">
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingTop: 6 }}>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={!!form.twic} onChange={(e) => set('twic', e.target.checked)} />
                TWIC
              </label>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={!!form.weekendOk} onChange={(e) => set('weekendOk', e.target.checked)} />
                Weekends
              </label>
            </div>
          </Field>
        </div>

        <div style={{ marginTop: 10 }}>
          <Field label="Notes" hint="Kept in the Project Tracker. A roster import never overwrites this.">
            <textarea
              style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
            />
          </Field>
        </div>

        {error && (
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--ew-danger)' }}>{error}</p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => { setError(null); save.mutate(); }}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', fontSize: 11, color: 'var(--ew-n-500)' }}>
      <span style={{ display: 'block', marginBottom: 3 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', marginTop: 3, fontSize: 10, lineHeight: 1.4 }}>{hint}</span>}
    </label>
  );
}
